'use client';

import { useEffect, useState } from 'react';
import { Eye, X } from 'lucide-react';
import {
  getImpersonation,
  setImpersonation,
  IMPERSONATION_ENDED_EVENT,
  type ImpersonationSession,
} from '@/lib/api';

/**
 * Always-visible marker that the session is borrowed.
 *
 * Without it an admin can forget whose account they are in — the whole reason
 * "View as" needs an obvious exit, not a hidden one. It sits above everything
 * and cannot be dismissed except by actually leaving. The server decides write
 * access from the signed mode and audits every mutation attempt; local browser
 * state only controls what this banner says.
 */
export function ImpersonationBanner() {
  const [session, setSession] = useState<ImpersonationSession | null>(null);

  useEffect(() => {
    let had = getImpersonation() !== null;
    const sync = () => {
      const next = getImpersonation();
      // Expiry tears down localStorage, but BusinessProvider still holds the
      // target's business in React state: without a reload the screen keeps
      // rendering the target while every new request resolves as the admin.
      // Reload the same way the explicit Stop path does.
      if (had && !next) {
        window.location.reload();
        return;
      }
      had = next !== null;
      setSession(next);
    };
    sync();
    // The grant lives in localStorage, so it applies to every open tab the
    // instant it is set — but only the tab that set it re-renders. Without the
    // storage listener another tab keeps sending the header while showing no
    // banner, which is exactly the "acting in someone else's account without
    // realising" case this banner exists to prevent. The interval stays as a
    // backstop for expiry, which fires no event.
    window.addEventListener(IMPERSONATION_ENDED_EVENT, sync);
    window.addEventListener('storage', sync);
    window.addEventListener('focus', sync);
    document.addEventListener('visibilitychange', sync);
    const id = window.setInterval(sync, 30_000);
    return () => {
      window.removeEventListener(IMPERSONATION_ENDED_EVENT, sync);
      window.removeEventListener('storage', sync);
      window.removeEventListener('focus', sync);
      document.removeEventListener('visibilitychange', sync);
      window.clearInterval(id);
    };
  }, []);

  if (!session) return null;

  const stop = () => {
    setImpersonation(null);
    window.location.href = '/admin/users';
  };

  const label = session.user.name || session.user.email || session.user.id;
  const minutesLeft = Math.max(0, Math.round((session.expiresAt - Date.now()) / 60_000));
  const canWrite = session.mode === 'support_write';

  return (
    <div
      role="status"
      className={`flex-none flex items-center justify-center gap-3 px-3 py-1.5 text-sm ${
        canWrite ? 'bg-orange-600 text-white' : 'bg-amber-500 text-amber-950'
      }`}
    >
      <Eye className="h-4 w-4 shrink-0" />
      <span className="truncate">
        Viewing as <strong>{label}</strong> —{' '}
        {canWrite
          ? 'support edit mode; changes apply to this account and are audited.'
          : 'read-only legacy session; restart View as to edit.'}{' '}
        Ends in {minutesLeft} min.
      </span>
      <button
        type="button"
        onClick={stop}
        className="inline-flex shrink-0 items-center gap-1 rounded bg-black/10 px-2 py-0.5 font-medium hover:bg-black/20"
      >
        <X className="h-3.5 w-3.5" />
        Stop
      </button>
    </div>
  );
}
